import { useEffect, useMemo, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { BookOpen, BookmarkSimple, Check, Plus, PencilSimple, Trash, ArrowUp, ArrowDown, GearSix, MagnifyingGlass } from "@phosphor-icons/react";

export const CATEGORY_LABELS = {
  general: "General", plumbing: "Plumbing", electrical: "Electrical", carpentry: "Carpentry", painting: "Painting & decorating", roofing: "Roofing",
  landscaping: "Landscaping", cleaning: "Cleaning", hvac: "Heating & ventilation", fire_safety: "Fire safety", security: "Security", mould_eradication: "Mould", other: "Other",
};
const label = (c) => CATEGORY_LABELS[c] || c;

/* Two libraries share one picker/manager: standard caveats and standard scope-of-works lines. */
export const KINDS = {
  caveat: { base: "/caveat-library", button: "Standard caveats", short: "Standard", title: "Standard caveats", noun: "caveat",
            desc: "Your library of exclusions and assumptions. Staff drop these into any Caveats box with one tap; the record's trade is shown first.",
            placeholder: "e.g. Excludes redecoration and making good beyond the immediate work area", search: "Search standard caveats…",
            empty: "The library is empty — add your standard caveats under Manage.",
            btn: "border-amber-300 text-amber-800 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-950/40", row: "hover:bg-amber-50 dark:hover:bg-amber-950/30", plus: "text-amber-600" },
  scope: { base: "/scope-library", button: "Standard scope lines", short: "Standard", title: "Standard scope lines", noun: "scope line",
           desc: "Common scope-of-works lines for standard jobs. Staff drop these into any Scope of Works box with one tap; the record's trade is shown first.",
           placeholder: "e.g. Isolate the water supply and drain down the affected section", search: "Search standard scope lines…",
           empty: "The library is empty — add your standard scope lines under Manage.",
           btn: "border-primary/40 text-primary hover:bg-primary/5", row: "hover:bg-primary/5", plus: "text-primary" },
};
const norm = (s) => (s || "").replace(/^[-*•]\s*/, "").trim().toLowerCase();
export const cleanLine = (s) => (s || "").replace(/^[-*•]\s*/, "").trim();
export const presentLines = (value) => new Set((value || "").split("\n").map(norm).filter(Boolean));
const CHANGED_EVENT = "phrase-library-changed";

/* Save a typed line into the library (right-click menu + "Save from this box"). Resolves true when stored. */
export async function saveToLibrary(kind, text, category) {
  const K = KINDS[kind] || KINDS.caveat;
  try {
    await API.post(K.base, { text: cleanLine(text), category: category || "general" });
    toast.success(`Saved to ${K.title.toLowerCase()} under ${label(category || "general")}`);
    window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: kind }));
    return true;
  } catch (e) {
    const d = e.response?.data?.detail || "";
    if (/already/i.test(String(d))) toast.info("That line is already in the library");
    else toast.error(formatApiError(d));
    return false;
  }
}

/* Group entries: the record's trade first, then General, then everything else alphabetically. */
export function groupEntries(entries, category) {
  const order = [category, "general", ...Object.keys(CATEGORY_LABELS)].filter((c, i, a) => c && a.indexOf(c) === i);
  const byCat = {};
  for (const e of entries) (byCat[e.category] ||= []).push(e);
  return order.filter((c) => byCat[c]?.length).map((c) => ({ category: c, label: label(c), entries: byCat[c] }));
}

/* "Standard caveats" button + popover pick-list. onPick(text) appends the caveat as a bullet line. */
export default function CaveatLibraryPicker({ value, onPick, category = "general", testid = "caveat-library", compact = false, kind = "caveat" }) {
  const K = KINDS[kind] || KINDS.caveat;
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(false);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const load = () => API.get(K.base).then(({ data }) => setData(data)).catch(() => setData({ entries: [], categories: [] }));
  useEffect(() => { if (open && !data) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onChanged = (e) => { if (e.detail === kind) { if (open) load(); else setData(null); } };
    window.addEventListener(CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CHANGED_EVENT, onChanged);
  }, [kind, open]); // eslint-disable-line react-hooks/exhaustive-deps
  const unsaved = useMemo(() => {
    if (!data) return [];
    const known = new Set((data.entries || []).map((e) => norm(e.text)));
    return [...new Set((value || "").split("\n").map(cleanLine).filter((l) => l.length >= 4))].filter((l) => !known.has(l.toLowerCase()));
  }, [data, value]);
  const present = useMemo(() => presentLines(value), [value]);
  const groups = useMemo(() => {
    const list = (data?.entries || []).filter((e) => !q.trim() || e.text.toLowerCase().includes(q.trim().toLowerCase()));
    return groupEntries(list, category);
  }, [data, q, category]);

  const pick = (e) => {
    if (present.has(norm(e.text))) return;
    onPick(e.text);
    API.post(`${K.base}/${e.id}/used`).catch(() => {});
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" size="sm" variant="outline" className={`h-7 text-xs shrink-0 ${K.btn}`} data-testid={`${testid}-btn`}>
            <BookOpen size={13} weight="fill" className="mr-1" /> {compact ? K.short : K.button}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[420px] max-w-[95vw] p-0" data-testid={`${testid}-popover`}>
          <div className="p-2 border-b border-border flex items-center gap-2">
            <MagnifyingGlass size={14} className="text-muted-foreground shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={K.search} className="flex-1 bg-transparent text-xs outline-none" data-testid={`${testid}-search`} />
            <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => { setOpen(false); setManage(true); }} data-testid={`${testid}-manage-btn`}>
              <GearSix size={12} className="mr-1" /> Manage
            </Button>
          </div>
          <div className="max-h-[360px] overflow-y-auto p-1" data-testid={`${testid}-list`}>
            {!data ? <p className="text-xs text-muted-foreground p-3">Loading…</p>
              : groups.length === 0 ? <p className="text-xs text-muted-foreground p-3">{q ? "Nothing matches that search." : K.empty}</p>
              : groups.map((g) => (
                <div key={g.category} className="mb-1">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold px-2 pt-2 pb-1">{g.label}{g.category === category && category !== "general" ? " · this trade" : ""}</p>
                  {g.entries.map((e) => {
                    const added = present.has(norm(e.text));
                    return (
                      <button key={e.id} type="button" onClick={() => pick(e)} disabled={added}
                              className={`w-full text-left text-xs px-2 py-1.5 rounded-md flex items-start gap-2 ${added ? "text-muted-foreground cursor-default" : K.row}`}
                              data-testid={`${testid}-entry-${e.id}`}>
                        {added ? <Check size={13} className="mt-0.5 shrink-0 text-emerald-600" /> : <Plus size={13} className={`mt-0.5 shrink-0 ${K.plus}`} />}
                        <span>{e.text}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            {data && !q && unsaved.length > 0 && (
              <div className="mt-1 border-t border-border pt-1" data-testid={`${testid}-unsaved`}>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold px-2 pt-2 pb-1">Save from this box</p>
                {unsaved.map((l) => (
                  <button key={l} type="button" onClick={async () => { if (await saveToLibrary(kind, l, category)) load(); }}
                          className="w-full text-left text-xs px-2 py-1.5 rounded-md flex items-start gap-2 hover:bg-muted" data-testid={`${testid}-save-line`}>
                    <BookmarkSimple size={13} className="mt-0.5 shrink-0 text-primary" />
                    <span><span className="text-muted-foreground">Save as standard: </span>{l}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
      <CaveatLibraryDialog open={manage} onOpenChange={setManage} onChanged={load} defaultCategory={category} kind={kind} />
    </>
  );
}

/* Library manager: add / edit / reorder / delete. */
export function CaveatLibraryDialog({ open, onOpenChange, onChanged, defaultCategory = "general", kind = "caveat" }) {
  const K = KINDS[kind] || KINDS.caveat;
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // {id?, text, category}
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await API.get(K.base); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { if (open) { setEditing(null); load(); } }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      if (editing.id) await API.put(`${K.base}/${editing.id}`, { text: editing.text, category: editing.category });
      else await API.post(K.base, { text: editing.text, category: editing.category });
      toast.success(editing.id ? "Entry updated" : "Added to the library");
      setEditing(null); await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (e) => {
    if (!window.confirm("Remove this entry from the library?")) return;
    try { await API.delete(`${K.base}/${e.id}`); toast.success("Removed"); await load(); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const move = async (idx, dir) => {
    const ids = data.entries.map((e) => e.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try { await API.put(`${K.base}/reorder`, { ids }); await load(); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="caveat-library-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">{K.title}</DialogTitle>
          <DialogDescription>{K.desc}</DialogDescription>
        </DialogHeader>
        {!data ? <p className="text-sm text-muted-foreground">Loading…</p> : editing ? (
          <div className="space-y-3" data-testid="caveat-library-form">
            <div>
              <Label className="text-xs">Trade</Label>
              <Select value={editing.category} onValueChange={(v) => setEditing((p) => ({ ...p, category: v }))}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="caveat-library-category"><SelectValue /></SelectTrigger>
                <SelectContent>{(data.categories || Object.keys(CATEGORY_LABELS)).map((c) => <SelectItem key={c} value={c}>{label(c)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs capitalize">{K.noun}</Label><Textarea value={editing.text} onChange={(e) => setEditing((p) => ({ ...p, text: e.target.value }))} rows={3} className="mt-1 text-sm" placeholder={K.placeholder} data-testid="caveat-library-text" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(null)} disabled={busy} data-testid="caveat-library-cancel">Cancel</Button>
              <Button size="sm" onClick={save} disabled={busy || (editing.text || "").trim().length < 4} data-testid="caveat-library-save">{busy ? "Saving…" : editing.id ? "Save changes" : `Add ${K.noun}`}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setEditing({ text: "", category: defaultCategory || "general" })} data-testid="caveat-library-add-btn"><Plus size={14} className="mr-1" /> Add {K.noun}</Button>
            </div>
            {data.entries.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-md">Nothing in this library yet.</p>}
            {data.entries.map((e, idx) => (
              <div key={e.id} className="border border-border rounded-md px-3 py-2 flex items-start gap-2" data-testid={`caveat-library-row-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label(e.category)}{e.uses ? ` · used ${e.uses}×` : ""}</p>
                  <p className="text-sm">{e.text}</p>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(idx, -1)} disabled={idx === 0} title="Move up" data-testid={`caveat-library-up-${e.id}`}><ArrowUp size={13} /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(idx, 1)} disabled={idx === data.entries.length - 1} title="Move down" data-testid={`caveat-library-down-${e.id}`}><ArrowDown size={13} /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing({ id: e.id, text: e.text, category: e.category })} title="Edit" data-testid={`caveat-library-edit-${e.id}`}><PencilSimple size={13} /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => remove(e)} title="Remove" data-testid={`caveat-library-delete-${e.id}`}><Trash size={13} /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* Shared append helper: adds "- text" on a new line, never duplicates. */
export const appendCaveat = (value, text) => {
  if (presentLines(value).has(norm(text))) return value || "";
  const line = `- ${text.trim()}`;
  return (value || "").trim() ? `${value.trimEnd()}\n${line}` : line;
};
